// Vollea — League Organiser "My Clubs" — join by code + list memberships
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoBusinessOutline, IoCheckmarkCircle, IoTimeOutline, IoArrowForward, IoFlash, IoTrophy } from "react-icons/io5";
import { Btn, Card, Input, Empty } from "../components/ui-vollea";
import CreateLeagueModal from "../components/CreateLeagueModal";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function OrganiserClubs() {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [independent, setIndependent] = useState(null); // null=loading, league obj, or false
  const [showCreate, setShowCreate] = useState(false);

  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinMsg, setJoinMsg] = useState("");
  const [joinOk, setJoinOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMemberships(await api.get("/organiser/clubs") || []); }
    catch { setMemberships([]); }
    try {
      const managed = await api.get("/leagues?managed=true");
      const indep = (managed || []).find((l) => !l.club_id && !l.is_trial);
      setIndependent(indep || false);
    } catch { setIndependent(false); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const join = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setJoining(true); setJoinMsg(""); setJoinOk(false);
    try {
      const r = await api.post("/clubs/join", { code: code.trim() });
      setJoinOk(true);
      const alreadyJoined = memberships.some((m) => m.club_name === r.club_name);
      if (r.status === "approved") {
        setJoinMsg(`You're already approved at ${r.club_name}.`);
      } else if (alreadyJoined) {
        setJoinMsg(`Your request at ${r.club_name} is still pending approval.`);
      } else {
        setJoinMsg(`Request sent to ${r.club_name}. The club admin needs to approve you.`);
      }
      setCode("");
      load();
    } catch (e) { setJoinOk(false); setJoinMsg(e.message || "Something went wrong — please try again."); }
    finally { setJoining(false); }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate(-1)} data-testid="organiser-clubs-back" style={{ color: C.text, marginBottom: SP.md }}>
        <IoChevronBack size={26} />
      </button>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>My Clubs</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4 }}>
        Join a club with its 6-character code to run leagues on their behalf.
      </p>

      {joinMsg ? (
        <div
          data-testid="organiser-clubs-join-msg"
          style={{
            marginTop: SP.lg,
            background: joinOk ? C.limeTint : "rgba(239,68,68,0.1)",
            border: `1px solid ${joinOk ? C.lime : C.error}`,
            color: joinOk ? C.lime : C.error,
            padding: SP.md, borderRadius: R.md, fontSize: 13, fontWeight: 600,
          }}
        >
          {joinMsg}
        </div>
      ) : null}

      <Card style={{ marginTop: SP.lg }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP.sm }}>
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <IoFlash size={12} /> Independent league
          </div>
          {independent && independent.code ? (
            <span style={{ background: C.limeTint, color: C.lime, fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "2px 8px", borderRadius: R.pill }}>Active</span>
          ) : null}
        </div>
        {independent === null ? (
          <div style={{ color: C.textMuted, fontSize: 12 }}>Checking…</div>
        ) : independent ? (
          <button
            onClick={() => navigate(`/leagues/${independent.league_id}`)}
            data-testid="organiser-independent-open"
            style={{ display: "flex", alignItems: "center", gap: SP.md, width: "100%", background: "transparent", padding: 0 }}
          >
            <div style={{ width: 40, height: 40, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IoTrophy size={20} color={C.lime} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{independent.name}</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{independent.member_count ?? 0} players · Code {independent.code}</div>
            </div>
            <IoArrowForward size={16} color={C.textDim} />
          </button>
        ) : (
          <>
            <p style={{ color: C.textMuted, fontSize: 13, margin: `0 0 ${SP.md}px` }}>
              You can run one independent league outside any club. Join a club instead for unlimited leagues.
            </p>
            <Btn
              label="CREATE MY LEAGUE"
              onClick={() => setShowCreate(true)}
              testid="organiser-create-independent"
              icon={<IoFlash size={16} />}
            />
          </>
        )}
      </Card>

      <Card style={{ marginTop: SP.lg }}>
        <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: SP.sm }}>Join with club code</div>
        <form onSubmit={join} style={{ display: "flex", gap: SP.sm }}>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 1U14OC"
            testid="organiser-clubs-code-input"
            autoCapitalize="characters"
            maxLength={12}
          />
          <Btn
            label="JOIN"
            onClick={join}
            type="submit"
            loading={joining}
            testid="organiser-clubs-join-button"
            style={{ width: 90, height: 52 }}
          />
        </form>
        <p style={{ color: C.textDim, fontSize: 12, marginTop: SP.sm, margin: `${SP.sm}px 0 0` }}>
          Ask the club admin for the code shown on their My Club page.
        </p>
      </Card>

      <div style={{ marginTop: SP.xl }}>
        <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2, marginBottom: SP.md, textTransform: "uppercase" }}>
          Memberships ({memberships.length})
        </div>
        <div data-testid="organiser-clubs-list" style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
          {loading ? (
            <div style={{ textAlign: "center", color: C.lime, padding: SP.xl }}>Loading…</div>
          ) : memberships.length === 0 ? (
            <Empty
              icon={<IoBusinessOutline size={36} color={C.textDim} />}
              text="You haven't joined any clubs yet. Paste a code above to request access."
              testid="organiser-clubs-empty"
            />
          ) : memberships.map((m) => (
            <Card
              key={m.co_id}
              testid={`organiser-club-row-${m.co_id}`}
              onClick={m.status === "approved" ? () => navigate(`/leagues`) : undefined}
              style={{ cursor: m.status === "approved" ? "pointer" : "default" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                <div style={{ width: 44, height: 44, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <IoBusinessOutline size={20} color={C.lime} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.club_name}</div>
                  <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>
                    {m.status === "approved" ? "You can run leagues for this club." : "Awaiting approval from the club admin."}
                  </div>
                </div>
                <MembershipChip status={m.status} testid={`organiser-club-status-${m.co_id}`} />
                {m.status === "approved" && <IoArrowForward size={18} color={C.textDim} />}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {showCreate && (
        <CreateLeagueModal
          mode="organiser"
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}

function MembershipChip({ status, testid }) {
  const conf = status === "approved"
    ? { bg: C.limeTint, fg: C.lime, text: "Approved", Icon: IoCheckmarkCircle }
    : { bg: "rgba(245,158,11,0.15)", fg: C.warning, text: "Pending", Icon: IoTimeOutline };
  const { Icon } = conf;
  return (
    <span data-testid={testid} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: conf.bg, color: conf.fg, fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>
      <Icon size={12} /> {conf.text}
    </span>
  );
}
